import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bell,
  ChevronDown,
  Settings,
  LogOut,
  Sun,
  Moon,
  Building2,
  Layers,
  Check,
  Loader2,
  ArrowRightLeft,
  HelpCircle,
  RefreshCw,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useView } from '../../contexts/ViewContext';
import { usePortalMode } from '../../hooks/usePortalMode';
import { notificationsAPI, Notification } from '../../lib/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { useHelp } from '../help';
import { usePageRefresh } from '../../contexts/PageRefreshContext';
import GlobalSearch from './GlobalSearch';

export default function TopBar() {
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { theme, toggleTheme } = useTheme();
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  // Portal mode for badge/label display
  const { portalMode } = usePortalMode();

  // Help context
  const { openPageHelp, openGlossary } = useHelp();

  // Page refresh — re-fetches current page data without navigating away
  const { isRefreshing, refresh } = usePageRefresh();

  // View context for company/site switching
  const {
    viewLevel,
    selectedGroupId,
    selectedGroupName,
    selectedCompanyName,
    selectedSiteName,
    supportsDualMode,
    isSaving,
    accessibleSites,
    accessibleCompanies,
    companyGroup,
    selectGroup,
    selectSite,
    selectCompany,
    togglePortalMode,
    hasSiteRestrictions,
  } = useView();

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const [recentResponse, countResponse] = await Promise.all([
        notificationsAPI.getRecent(),
        notificationsAPI.getUnreadCount(),
      ]);
      setNotifications(recentResponse.data);
      setUnreadCount(countResponse.data.unread_count);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 60 seconds
    const interval = setInterval(fetchNotifications, 60000);
    // CR-3938: Listen for notification changes from other pages (e.g. Notifications page)
    const handleNotificationsUpdated = () => fetchNotifications();
    window.addEventListener('notifications-updated', handleNotificationsUpdated);
    return () => {
      clearInterval(interval);
      window.removeEventListener('notifications-updated', handleNotificationsUpdated);
    };
  }, [fetchNotifications]);

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read if unread
    if (!notification.is_read) {
      try {
        await notificationsAPI.markAsRead(notification.id);
        setNotifications(prev =>
          prev.map(n => (n.id === notification.id ? { ...n, is_read: true } : n))
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
    // Navigate to action URL if available
    if (notification.action_url) {
      setNotificationsOpen(false);
      navigate(notification.action_url);
    }
  };

  const handleLogout = () => {
    // Use AuthContext logout which clears state, localStorage, and calls API
    logout();
    // Redirect to login page
    navigate('/login');
  };

  return (
    <header data-tour="topbar" className="sticky top-0 z-30 bg-white dark:bg-card border-b border-gray-200 dark:border-border" role="banner">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left section - Search (CR-3954) */}
          <div className="flex-1 flex items-center pl-12 md:pl-0">
            <GlobalSearch />
          </div>

          {/* Right section */}
          <div className="ml-4 flex items-center space-x-2">
            {/* Refresh — re-fetches current page data in place */}
            <Button
              variant="ghost"
              size="icon"
              onClick={refresh}
              disabled={isRefreshing}
              aria-label="Refresh page data"
              title="Refresh"
              className="hover:bg-gray-100 dark:hover:bg-accent"
            >
              <RefreshCw className={`h-5 w-5 text-gray-600 dark:text-muted-foreground ${isRefreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
            </Button>

            {/* Theme toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              className="hover:bg-gray-100 dark:hover:bg-accent"
            >
              {theme === 'light' ? (
                <Moon className="h-5 w-5 text-gray-600 dark:text-muted-foreground" aria-hidden="true" />
              ) : (
                <Sun className="h-5 w-5 text-gray-600 dark:text-muted-foreground" aria-hidden="true" />
              )}
            </Button>


            {/* Help button */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:bg-gray-100 dark:hover:bg-accent"
                  aria-label="Help"
                >
                  <HelpCircle className="h-5 w-5 text-gray-600 dark:text-muted-foreground" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={openPageHelp} className="cursor-pointer">
                  Page Help
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => openGlossary()} className="cursor-pointer">
                  Glossary
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Notifications */}
            <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative hover:bg-gray-100 dark:hover:bg-accent"
                  aria-label="Notifications"
                >
                  <Bell className="h-5 w-5 text-gray-600 dark:text-muted-foreground" aria-hidden="true" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-white dark:ring-background" aria-label={`${unreadCount} new notifications`} />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0">
                <div className="p-4 border-b border-gray-200 dark:border-border flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-foreground">Notifications</h3>
                  {unreadCount > 0 && (
                    <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-2 py-0.5 rounded-full">
                      {unreadCount} new
                    </span>
                  )}
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-gray-500 dark:text-muted-foreground">
                      <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No notifications</p>
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={cn(
                          'px-4 py-3 hover:bg-gray-50 dark:hover:bg-accent cursor-pointer transition-colors',
                          !notification.is_read && 'bg-blue-50 dark:bg-primary/10'
                        )}
                        tabIndex={0}
                        role="button"
                        aria-label={`${notification.title}. ${notification.message}. ${notification.time_ago}`}
                        onClick={() => handleNotificationClick(notification)}
                        onKeyDown={(e) => e.key === 'Enter' && handleNotificationClick(notification)}
                      >
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <p className={cn(
                              'text-sm text-gray-900 dark:text-foreground',
                              !notification.is_read && 'font-medium'
                            )}>
                              {notification.title}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-muted-foreground line-clamp-2">{notification.message}</p>
                            <p className="text-xs text-gray-500 dark:text-muted-foreground mt-1">{notification.time_ago}</p>
                          </div>
                          {!notification.is_read && (
                            <span className="h-2 w-2 rounded-full bg-blue-500 mt-2" />
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="p-3 border-t border-gray-200 dark:border-border">
                  <Link
                    to="/notifications"
                    className="text-sm text-blue-600 hover:text-blue-700 dark:text-primary dark:hover:text-primary/80 font-medium"
                    onClick={() => setNotificationsOpen(false)}
                  >
                    View all notifications
                  </Link>
                </div>
              </PopoverContent>
            </Popover>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center hover:bg-gray-100 dark:hover:bg-accent"
                  aria-label="User menu"
                >
                  <span
                    className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold mr-2 border bg-white text-gray-900 border-gray-300 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                  >
                    {portalMode === 'supplier' ? 'Supplier' : 'Customer'}
                  </span>
                  <div className="h-8 w-8 rounded-full bg-blue-600 dark:bg-primary flex items-center justify-center text-white dark:text-primary-foreground font-medium mr-2">
                    {(user?.username || user?.full_name) ? (user.username || user.full_name).split(' ').map((n: string) => n[0]).join('') : 'G'}
                  </div>
                  <div className="hidden md:flex flex-col items-start mr-1">
                    <span className="text-gray-700 dark:text-foreground font-medium text-sm leading-tight">{user?.username || user?.full_name || 'Guest'}</span>
                    <span className="text-xs text-muted-foreground leading-tight">
                      {viewLevel === 'GROUP' && selectedGroupName ? (
                        <>{selectedGroupName}</>
                      ) : viewLevel === 'SITE' && selectedSiteName ? (
                        <>{selectedSiteName}</>
                      ) : selectedCompanyName ? (
                        <>{selectedCompanyName}</>
                      ) : user?.companyName ? (
                        <>{user.companyName}</>
                      ) : (
                        'No context'
                      )}
                    </span>
                  </div>
                  <ChevronDown className="ml-1 h-4 w-4 text-gray-500 dark:text-muted-foreground" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-foreground">{user?.username || user?.full_name || 'Guest'}</p>
                    <p className="text-xs text-gray-600 dark:text-muted-foreground">{user?.email || ''}</p>
                  </div>
                </DropdownMenuLabel>

                {/* Data Context Switcher - hierarchical view selection */}
                {(accessibleCompanies.length > 0 || companyGroup || accessibleSites.length > 0) && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel data-tour="view-selector" className="flex items-center justify-between">
                      <span className="text-xs uppercase tracking-wide text-muted-foreground">
                        View Data For
                      </span>
                      {isSaving && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                    </DropdownMenuLabel>

                    {/* CR-4541 / CR-5315: Group option — surface for both
                        unrestricted multi-company users and group-level users
                        (whose hasSiteRestrictions is true because CR-5311
                        gave them an explicit UserSitePermission per site). */}
                    {companyGroup && (
                      (!hasSiteRestrictions && accessibleCompanies.length > 1)
                      || (hasSiteRestrictions && accessibleSites.length > 1)
                    ) && (
                      <DropdownMenuItem
                        onClick={() => selectGroup(companyGroup.id, companyGroup.name)}
                        className={cn(
                          "cursor-pointer",
                          viewLevel === 'GROUP' && selectedGroupId === companyGroup.id && "bg-primary/15 text-primary"
                        )}
                      >
                        <Layers className={cn("mr-2 h-4 w-4", viewLevel === 'GROUP' && selectedGroupId === companyGroup.id ? "text-primary" : "text-purple-500")} />
                        <div className="flex-1 min-w-0">
                          <span className={cn("block whitespace-normal break-words", viewLevel === 'GROUP' && selectedGroupId === companyGroup.id && "font-semibold")}>
                            {companyGroup.name}
                          </span>
                        </div>
                        {viewLevel === 'GROUP' && selectedGroupId === companyGroup.id && <Check className="h-4 w-4 text-primary ml-1" />}
                      </DropdownMenuItem>
                    )}

                    {/* CR-4541: Divisions (sub-groups) with nested sites */}
                    {/* Group companies by their group (division) and show as a hierarchy */}
                    {!hasSiteRestrictions && (() => {
                      // Build division map: group companies by their group_name
                      const divisions = new Map<string, typeof accessibleCompanies>();
                      const ungrouped: typeof accessibleCompanies = [];
                      for (const company of accessibleCompanies) {
                        const groupName = (company as unknown as Record<string, unknown>)['group_name'] as string | undefined;
                        const groupId = (company as unknown as Record<string, unknown>)['group'] as number | undefined;
                        if (groupName && groupId) {
                          const key = `${groupId}:${groupName}`;
                          if (!divisions.has(key)) divisions.set(key, []);
                          divisions.get(key)!.push(company);
                        } else {
                          ungrouped.push(company);
                        }
                      }

                      // If all companies are in the same group (no sub-divisions), render flat
                      if (divisions.size <= 1 && ungrouped.length === 0) {
                        return accessibleCompanies.map((company) => {
                          const isSelected = viewLevel === 'COMPANY' && company.name === selectedCompanyName;
                          return (
                            <DropdownMenuItem
                              key={company.id}
                              onClick={() => selectCompany(company.id, company.name)}
                              className={cn(
                                "cursor-pointer pl-6",
                                isSelected && "bg-primary/15 text-primary"
                              )}
                            >
                              <Building2 className={cn("mr-2 h-4 w-4", isSelected ? "text-primary" : "text-blue-500")} />
                              <span className={cn("flex-1 whitespace-normal break-words", isSelected && "font-semibold")}>
                                {company.name}
                              </span>
                              {isSelected && <Check className="h-4 w-4 text-primary ml-1" />}
                            </DropdownMenuItem>
                          );
                        });
                      }

                      // Multiple divisions: render hierarchy (Division → Sites)
                      return (
                        <>
                          {Array.from(divisions.entries()).map(([key, companies]) => {
                            const divisionName = key.split(':').slice(1).join(':');
                            return (
                              <div key={key}>
                                {/* Division header — clickable to select division-level rollup */}
                                {(() => {
                                  const groupId = parseInt(key.split(':')[0], 10);
                                  const isDivisionSelected = viewLevel === 'GROUP' && selectedGroupId === groupId;
                                  return (
                                    <DropdownMenuItem
                                      onClick={() => { if (groupId) selectGroup(groupId, divisionName); }}
                                      className={cn("cursor-pointer pl-6", isDivisionSelected && "bg-primary/15 text-primary")}
                                    >
                                      <Building2 className={cn("mr-2 h-4 w-4", isDivisionSelected ? "text-primary" : "text-indigo-500")} />
                                      <span className={cn("text-xs font-semibold uppercase tracking-wide", isDivisionSelected && "text-primary")}>
                                        {divisionName}
                                      </span>
                                      {isDivisionSelected && <Check className="h-4 w-4 text-primary ml-1" />}
                                    </DropdownMenuItem>
                                  );
                                })()}
                                {/* Sites within this division */}
                                {companies.map((company) => {
                                  const isSelected = viewLevel === 'COMPANY' && company.name === selectedCompanyName;
                                  return (
                                    <DropdownMenuItem
                                      key={company.id}
                                      onClick={() => selectCompany(company.id, company.name)}
                                      className={cn(
                                        "cursor-pointer pl-10",
                                        isSelected && "bg-primary/15 text-primary"
                                      )}
                                    >
                                      <Building2 className={cn("mr-2 h-4 w-4", isSelected ? "text-primary" : "text-blue-500")} />
                                      <span className={cn("flex-1 whitespace-normal break-words", isSelected && "font-semibold")}>
                                        {company.name}
                                      </span>
                                      {isSelected && <Check className="h-4 w-4 text-primary ml-1" />}
                                    </DropdownMenuItem>
                                  );
                                })}
                              </div>
                            );
                          })}
                          {/* Ungrouped companies */}
                          {ungrouped.map((company) => {
                            const isSelected = viewLevel === 'COMPANY' && company.name === selectedCompanyName;
                            return (
                              <DropdownMenuItem
                                key={company.id}
                                onClick={() => selectCompany(company.id, company.name)}
                                className={cn(
                                  "cursor-pointer pl-6",
                                  isSelected && "bg-primary/15 text-primary"
                                )}
                              >
                                <Building2 className={cn("mr-2 h-4 w-4", isSelected ? "text-primary" : "text-blue-500")} />
                                <span className={cn("flex-1 whitespace-normal break-words", isSelected && "font-semibold")}>
                                  {company.name}
                                </span>
                                {isSelected && <Check className="h-4 w-4 text-primary ml-1" />}
                              </DropdownMenuItem>
                            );
                          })}
                        </>
                      );
                    })()}

                    {/* Sites without a matching division (site-restricted users) */}
                    {hasSiteRestrictions && accessibleSites.map((site) => {
                      const isSelected = viewLevel === 'SITE' && site.name === selectedSiteName;
                      return (
                        <DropdownMenuItem
                          key={site.id}
                          onClick={() => selectSite(site.id, site.name)}
                          className={cn(
                            "cursor-pointer pl-6",
                            isSelected && "bg-primary/15 text-primary"
                          )}
                        >
                          <Building2 className={cn("mr-2 h-4 w-4", isSelected ? "text-primary" : "text-blue-500")} />
                          <span className={cn("flex-1 whitespace-normal break-words", isSelected && "font-semibold")}>
                            {site.name}
                          </span>
                          {isSelected && <Check className="h-4 w-4 text-primary ml-1" />}
                        </DropdownMenuItem>
                      );
                    })}
                  </>
                )}

                {/* Portal Mode Toggle (Customer/Supplier) */}
                {supportsDualMode && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={togglePortalMode} className="cursor-pointer">
                      <ArrowRightLeft className="mr-2 h-4 w-4 text-muted-foreground" />
                      <span>Switch to {portalMode === 'customer' ? 'Supplier' : 'Customer'} View</span>
                    </DropdownMenuItem>
                  </>
                )}

                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="flex items-center cursor-pointer">
                    <Settings className="mr-2 h-4 w-4 text-gray-500 dark:text-muted-foreground" aria-hidden="true" />
                    My Account
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="flex items-center text-red-600 dark:text-destructive focus:text-red-600 dark:focus:text-destructive focus:bg-red-50 dark:focus:bg-destructive/10 cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}
